import {
  Box,
  Button,
  Card,
  Typography
} from '@mui/material';
import { userRoles } from 'src/common/config/TextMessage';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import { useState } from 'react';
import OrbitAIAddonModal from './OrbitAIAddonModal';
import { OrbitAiSidebar } from 'src/content/onBoarding/orbitAiStepUtils';
import { orbitAiStepCSS } from 'src/content/onBoarding/OrbitAiStepCSS';
import EastIcon from '@mui/icons-material/East';
import { paidPlanOnBoardingSxs } from 'src/content/onBoarding/OnBoardingCSS';
import { openNewTab } from 'src/common/config/FunctionHelper';
import { priceOrbitText } from 'src/content/dashboard/TextMessages';

interface UpgradeToOrbitAIProps {
  children?: React.ReactNode;
  sidebar?: React.ReactNode;
}

interface SkipButtonProps {
  onClick?: () => void;
}

const bannerItems = [
  {
    dotColor: 'bg-secondary',
    text: '4 review replies drafted and sent',
    tag: '· Review Automation',
    status: 'Done',
    statusBg: 'bg-[#DDFDF6]',
    statusColor: 'text-[#0D7A63]'
  },
  {
    dotColor: 'bg-[#FEBB1A]',
    text: '2 pages competing for "linen bedding"',
    tag: '· Keyword Assist',
    status: 'Needs you',
    statusBg: 'bg-[#FFF5D9]',
    statusColor: 'text-[#8A6400]'
  },
  {
    dotColor: 'bg-[#DD588D]',
    text: '3 topics added to your content plan',
    tag: '· Content Strategy',
    status: 'New',
    statusBg: 'bg-[#F5F0FE]',
    statusColor: 'text-[#130243]'
  }
];

const SkipButton = ({ onClick }: SkipButtonProps) => (
  <div className='flex items-center justify-center mt-4'>
      <Typography
        sx={paidPlanOnBoardingSxs?.skipOnBoardingBtnTextSx}
        onClick={onClick}
        className='cursor-pointer'
      >
        Continue without Orbit AI
      </Typography>
  </div>
);

const Sidebar = () => <OrbitAiSidebar />;

const UpgradeToOrbitAIBase = ({ children, sidebar }: UpgradeToOrbitAIProps) => {
  const isEnterprisePlan = useSelector(
    (state: RootState) =>
      state.user?.isEnterprisePlan ||
      state.user?.activePlan === state.user?.planDetails?.enterprise
  );

  const userDetails: any = useSelector(
    (state: RootState) => state.counter.userDetails
  );
  const isMember = userDetails?.role_type === userRoles?.member;

  const [openAddOnModal, setOpenAddOnModal] = useState(false);

  if (isEnterprisePlan) return null;

  const handleCloseAddOnModal = () => {
    setOpenAddOnModal(false);
  };

  return (
    <>
      <Box sx={orbitAiStepCSS?.rootSec}>
        {/* Left config side */}
        <Card
          sx={{
            flex: 2,
            borderRadius: '12px',
            boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
            border: '1px solid #EAECF0',
            height: 'max-content'
          }}
        >

          {/* Top Header */}
          <div className='p-6 border-b border-[#E0E0E6]'>
            <div className="text-center flex justify-center items-center gap-1.5 mb-2">
              <span className="font-inter font-medium text-[length:var(--font-size-18)] leading-[var(--line-height-28)] text-center text-[color:var(--color-text-muted)]">
                Set Up
              </span>
              <span className="font-inter font-bold text-[length:var(--font-size-18)] leading-[var(--line-height-28)] text-center bg-[linear-gradient(180deg,var(--color-primary)_0%,var(--color-secondary)_170%)] bg-clip-text text-transparent">
                Orbit AI
              </span>
              <span className="bg-[#E0FFFF] text-[#00B8D9] px-2 rounded-[20px] text-[12px] border border-[#00B8D9] ml-[2px] tracking-[0.4px]">
                NEW
              </span>
            </div>
            <div className="text-center">
              <p className="text-[color:var(--color-text-secondary)] text-[14px] font-normal leading-[20px]">
                Let AI guide you along the way.
              </p>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-6 pt-0 bg-[linear-gradient(180deg,#ECFDF3_0%,#FFFFFF_100%)]">
            {/* Content Top */}
            <div className="bg-themeWhite rounded-b-lg shadow-[0_6px_12px_2px_rgba(0,0,0,0.08)] max-w-max mx-auto select-none pointer-events-none">
              {bannerItems.map((item, index) => (
                <div
                  key={index}
                  className="p-3 flex items-center justify-between gap-12"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-2 w-2 rounded-full ${item.dotColor} shrink-0`}
                    />
                    <p className="text-xs font-medium text-[#373F41]">
                      {item.text}{' '}
                      <span className="text-[var(--color-text-secondary)] block text-xs font-normal sm:inline-block ml-2">
                        {item.tag}
                      </span>
                    </p>
                  </div>
                  <span
                    className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${item.statusBg} ${item.statusColor} shrink-0`}
                  >
                    {item.status}
                  </span>
                </div>
              ))}
            </div>


            <div className="mt-4">
              <div className='text-justify pt-4'>
                <div className="flex flex-col gap-2">
                  <p className="text-base font-semibold">
                    {priceOrbitText?.modalTitle}
                  </p>
                  <p className="text-sm text-[var(--color-text-secondary)]">
                    {priceOrbitText?.modalDesc}
                  </p>
                  <p
                    className="group text-sm text-primary font-semibold cursor-pointer inline-flex items-center gap-1 max-w-max"
                    onClick={() => openNewTab('https://www.youtube.com/watch?v=NV_1DmzA0yk')}
                  >
                    Watch Our Video Guide
                    <span className="transition-transform duration-300 group-hover:translate-x-1">
                      <EastIcon sx={{ fontSize: '18px' }} />
                    </span>
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Button
                  disabled={isMember}
                  variant="contained"
                  className="w-full h-12"
                  onClick={() => setOpenAddOnModal(true)}
                >
                  Buy Add-On
                </Button>
              </div>

              {children}

            </div>

          </div>
        </Card>
        {/* Right Section */}
        {sidebar}
      </Box>


      <OrbitAIAddonModal
        open={openAddOnModal}
        onClose={handleCloseAddOnModal}
      />
    </>
  );
};

export const UpgradeToOrbitAI = Object.assign(UpgradeToOrbitAIBase, {
  SkipButton,
  Sidebar,
});

export default UpgradeToOrbitAI;

